import { Button, Form, Input, Space } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectSelectedTenantId } from "../../../store/user/selectors";
import Modal from "../../../components/generic/Modal";
import { editTopic, saveTopic } from "../../../store/topics/thunks";
import Select from "../../../components/generic/Select";
import { StyledTopicModalDiv } from "./styles";
import TopicQuestions from "./TopicQuestions";

const AddEditTopicModal = (props) => {
  const { selectedTopic, handleCancel, branches } = props;

  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const selectedTenantId = useSelector(selectSelectedTenantId);
  const isEdit = useMemo(() => !!selectedTopic?.id, [selectedTopic]);

  useEffect(() => {
    if (selectedTopic.id) {
      form.setFieldsValue({
        ...selectedTopic,
        questions: selectedTopic.questions
          ? [...selectedTopic.questions].sort(
              (a, b) => (a?.order || 0) - (b?.order || 0),
            )
          : [],
      });
    }
  }, [selectedTopic]);

  const handleSubmit = useCallback(
    async (values) => {
      try {
        setSubmitting(true);

        await dispatch(
          isEdit
            ? editTopic({ ...values, topic_id: selectedTopic.id })
            : saveTopic({
                ...values,
                tenant_id: selectedTenantId,
              }),
        );

        handleCancel();
      } finally {
        setSubmitting(false);
      }
    },
    [isEdit, selectedTopic, selectedTenantId],
  );

  const branchOptions = useMemo(() => {
    return branches.map((branch) => ({
      label: branch.name,
      value: branch.id,
    }));
  }, [branches]);

  return (
    <Modal
      isProcessing={submitting}
      title={isEdit ? "Edit Topic" : "Add New Topic"}
      onCancel={handleCancel}
      footer={null}
      width={600}
      open
    >
      <StyledTopicModalDiv>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          disabled={submitting}
          initialValues={{
            questions: [
              {
                id: `${Date.now()}-${Math.random()}`,
                question: "",
                type: "string",
                options: [],
                order: 1,
              },
            ],
          }}
        >
          <Form.Item
            label="Topic Name"
            name="name"
            rules={[
              { required: true, message: "Please enter topic name" },
              { max: 100, message: "Topic name cannot exceed 100 characters" },
            ]}
          >
            <Input placeholder="Enter topic name" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: "Please enter description" }]}
          >
            <Input.TextArea rows={4} placeholder="Enter topic description" />
          </Form.Item>

          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: "Please select status" }]}
          >
            <Select
              placeholder="Select status"
              options={[
                { label: "Active", value: "active" },
                { label: "Inactive", value: "inactive" },
              ]}
            />
          </Form.Item>
          <TopicQuestions form={form} />

          <Form.Item
            label="Assign Branches"
            name="branch_ids"
            rules={[
              { required: true, message: "Please select at least one branch" },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Select branches"
              allowClear
              showSearch
              options={branchOptions}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 30 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={handleCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {isEdit ? "Update" : "Add"} Topic
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </StyledTopicModalDiv>
    </Modal>
  );
};

export default AddEditTopicModal;
